import React, { useState } from 'react';
import {
    Box, Typography, Container, Grid, Paper, Tabs, Tab, Button, useTheme, Card, CardHeader, CardContent, Divider, Chip, Tooltip, IconButton, TextField, InputAdornment, List, ListItem, ListItemText, Avatar
} from '@mui/material';
import {
    Support as HelpdeskIcon, FolderSpecial as KnowledgeIcon, Receipt as TicketIcon, Search as SearchIcon, 
    Add as AddIcon, Edit as EditIcon, Refresh as RefreshIcon, Visibility as ViewIcon, Archive as ArchiveIcon,
    ArrowUpward as PriorityUpIcon, ArrowDownward as PriorityDownIcon
} from '@mui/icons-material';

// --- MOCK Data ---
const mockTickets = [
    { id: 1001, subject: 'Payroll adjustment missing for Oct', status: 'Open', priority: 'High', agent: 'Alice', created: '2h ago', department: 'Finance' },
    { id: 1002, subject: 'VPN access revoked after password reset', status: 'Pending', priority: 'Critical', agent: 'Bob', created: '1d ago', department: 'IT' },
    { id: 1003, subject: 'Request for new keyboard model', status: 'New', priority: 'Low', agent: 'Unassigned', created: '4h ago', department: 'Assets' },
    { id: 1004, subject: 'Question about PTO balance calculation', status: 'Resolved', priority: 'Medium', agent: 'Alice', created: '3d ago', department: 'HR' },
];

const mockArticles = [
    { id: 201, title: 'How to Reset Your HRMS Password', views: 850, status: 'Published', category: 'IT' },
    { id: 202, title: 'Guide to Filing Expense Reports', views: 320, status: 'Published', category: 'Finance' },
    { id: 203, title: '2024 Policy on Remote Work', views: 15, status: 'Draft', category: 'Policy' },
];

const priorityColorMap = {
    Critical: 'error',
    High: 'warning',
    Medium: 'info',
    Low: 'default',
};
const statusColorMap = {
    Open: 'error',
    Pending: 'warning',
    New: 'primary',
    Resolved: 'success',
};


// --- Sub-Component: Ticket Management Panel ---
const TicketManagement = ({ theme }) => {
    return (
        <Card sx={{ minHeight: 600 }}>
            <CardHeader
                avatar={<TicketIcon color="primary" />}
                title={<Typography variant="h6" fontWeight={700} color="primary.dark">Open & Pending Tickets</Typography>}
                action={
                    <Box>
                        <Button startIcon={<AddIcon />} variant="contained" size="small" sx={{ mr: 1 }}>New Ticket</Button>
                        <IconButton><RefreshIcon /></IconButton>
                    </Box>
                }
                sx={{ borderBottom: `1px solid ${theme.palette.divider}`, bgcolor: theme.palette.grey[50] }}
            />
            <CardContent>
                <TextField
                    fullWidth
                    size="small"
                    placeholder="Search by ID, Subject, or Agent..."
                    sx={{ mb: 2 }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon color="action" />
                            </InputAdornment>
                        ),
                    }}
                />
                <List dense>
                    {mockTickets.map((ticket) => (
                        <React.Fragment key={ticket.id}>
                            <ListItem sx={{ '&:hover': { bgcolor: theme.palette.action.hover }, py: 1 }}>
                                <Grid container spacing={1} alignItems="center">
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="body1" fontWeight={600} sx={{ lineHeight: 1.2 }}>
                                            #{ticket.id}: {ticket.subject}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            Created: {ticket.created} | Dept: {ticket.department}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={6} sm={2}>
                                        <Chip label={ticket.status} size="small" color={statusColorMap[ticket.status]} />
                                    </Grid>
                                    <Grid item xs={6} sm={2}>
                                        <Chip 
                                            label={ticket.priority} 
                                            size="small" 
                                            color={priorityColorMap[ticket.priority]} 
                                            icon={ticket.priority === 'Critical' ? <PriorityUpIcon /> : <PriorityDownIcon />}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={2} sx={{ textAlign: { xs: 'left', sm: 'right' } }}>
                                        <Button size="small" variant="outlined" startIcon={<ViewIcon />} sx={{ minWidth: 'auto', px: 1 }}>
                                            View
                                        </Button>
                                    </Grid>
                                </Grid>
                            </ListItem>
                            <Divider component="li" />
                        </React.Fragment>
                    ))}
                </List>
            </CardContent>
        </Card>
    );
};

// --- Sub-Component: Knowledge Base Panel ---
const KnowledgeBaseManagement = ({ theme }) => {
    return (
        <Card sx={{ minHeight: 600 }}>
            <CardHeader
                avatar={<KnowledgeIcon color="secondary" />}
                title={<Typography variant="h6" fontWeight={700} color="secondary.dark">Knowledge Base Management</Typography>}
                action={
                    <Box>
                        <Button startIcon={<AddIcon />} variant="contained" color="secondary" size="small" sx={{ mr: 1 }}>New Article</Button>
                        <IconButton><RefreshIcon /></IconButton>
                    </Box>
                }
                sx={{ borderBottom: `1px solid ${theme.palette.divider}`, bgcolor: theme.palette.grey[50] }}
            />
            <CardContent>
                <TextField
                    fullWidth
                    size="small"
                    placeholder="Search Knowledge Base articles..."
                    sx={{ mb: 2 }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon color="action" />
                            </InputAdornment>
                        ),
                    }}
                />
                <List dense>
                    {mockArticles.map((article) => (
                        <React.Fragment key={article.id}>
                            <ListItem sx={{ '&:hover': { bgcolor: theme.palette.action.hover }, py: 1 }}>
                                <ListItemText 
                                    primary={<Typography variant="body1" fontWeight={600}>{article.title}</Typography>} 
                                    secondary={`Category: ${article.category} | Views: ${article.views.toLocaleString()}`} 
                                />
                                <Chip label={article.status} size="small" color={article.status === 'Draft' ? 'default' : 'success'} sx={{ mr: 2 }} />
                                <Button size="small" startIcon={<EditIcon />} sx={{ mr: 1 }}>Edit</Button>
                                <Button size="small" startIcon={<ArchiveIcon />} color="error">Archive</Button>
                            </ListItem>
                            <Divider component="li" />
                        </React.Fragment>
                    ))}
                </List>
            </CardContent>
        </Card>
    );
};


// --- Main Helpdesk Support Component ---
const HelpdeskSupport = () => {
    const theme = useTheme();
    const [tabValue, setTabValue] = useState(0);

    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
    };

    return (
        <Container maxWidth="xl" sx={{ py: 4 }}>
            {/* Header */}
            <Box sx={{ mb: 4 }}>
                <Typography 
                    variant="h4" 
                    component="h1" 
                    fontWeight={700} 
                    color="primary.dark"
                    sx={{ fontSize: { xs: '1.75rem', md: '2.5rem' } }}
                >
                    <HelpdeskIcon sx={{ mr: 1, verticalAlign: 'middle', fontSize: 'inherit' }} />
                    Helpdesk & Support Panel
                </Typography>
                <Typography variant="body1" color="text.secondary">
                    Manage support tickets and maintain the internal knowledge base for employees.
                </Typography>
            </Box>
            <Divider sx={{ mb: 4 }} />

            {/* Main Content Tabs */}
            <Paper elevation={4} sx={{ borderRadius: 3, overflow: 'hidden' }}>
                <Tabs value={tabValue} onChange={handleTabChange} aria-label="support tabs" sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
                    <Tab label="Ticket Management" icon={<TicketIcon />} iconPosition="start" />
                    <Tab label="Knowledge Base" icon={<KnowledgeIcon />} iconPosition="start" />
                </Tabs>

                <Box role="tabpanel" hidden={tabValue !== 0} id={`tabpanel-0`}>
                    {tabValue === 0 && (
                        <Box sx={{ p: 3 }}>
                            <TicketManagement theme={theme} />
                        </Box>
                    )}
                </Box>
                
                <Box role="tabpanel" hidden={tabValue !== 1} id={`tabpanel-1`}>
                    {tabValue === 1 && (
                        <Box sx={{ p: 3 }}>
                            <KnowledgeBaseManagement theme={theme} />
                        </Box>
                    )}
                </Box>
            </Paper>
        </Container>
    );
};

export default HelpdeskSupport;